import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, switchMap, tap, catchError, throwError } from 'rxjs';
import { KeycloakService } from '../services/keycloak.service';
import { API_ENDPOINT } from './env';
import { PendingRequestsService } from './pending-requests.service';

/**
 * Functional HTTP interceptor replacing HttpWanaka.
 * - Prepends API_ENDPOINT to relative URLs
 * - Adds Authorization header with Keycloak token
 * - Refreshes token before each request
 * - Adds Accept-Language header when i18n is configured
 * - Tracks pending requests for loading spinner
 */
export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const pendingRequests = inject(PendingRequestsService);

  // Snapshot the body synchronously: the legacy Http serialized with JSON.stringify at
  // call time, and several components rely on that (pattern "null the field -> save ->
  // restore {id: null} after subscribe"). Since the token refresh below is async, without
  // a snapshot the body would be serialized AFTER the restore and we would send
  // {id: null} refs (TransientPropertyValueException on the server).
  const body = req.body;
  if (
    body !== null &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer) &&
    !(body instanceof URLSearchParams)
  ) {
    req = req.clone({ body: JSON.parse(JSON.stringify(body)) });
  }

  // Skip auth for requests explicitly marked or absolute URLs
  if (req.url.startsWith('http://') || req.url.startsWith('https://')) {
    return next(req);
  }

  // Prepend API endpoint for relative URLs
  const apiUrl = API_ENDPOINT + req.url;

  // If not logged in, send without auth
  if (!KeycloakService.auth.loggedIn) {
    const cloned = req.clone({ url: apiUrl });
    pendingRequests.increment();
    return next(cloned).pipe(
      tap({ complete: () => pendingRequests.decrement() }),
      catchError((err) => {
        pendingRequests.decrement();
        return throwError(() => err);
      }),
    );
  }

  // Refresh token and add auth header
  return from(KeycloakService.updateToken()).pipe(
    switchMap((token) => {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };

      // Add Content-Type for mutation methods if not already set
      if (['POST', 'PUT', 'PATCH'].includes(req.method) && !req.headers.has('Content-Type')) {
        headers['Content-Type'] = 'application/json';
      }

      // Add Accept-Language if configured
      const lang = KeycloakService.i18nAcceptLanguageHeader;
      if (lang) {
        headers['Accept-Language'] = lang;
      }

      const cloned = req.clone({
        url: apiUrl,
        setHeaders: headers,
      });

      pendingRequests.increment();
      return next(cloned).pipe(
        tap({ complete: () => pendingRequests.decrement() }),
        catchError((err) => {
          pendingRequests.decrement();
          return throwError(() => err);
        }),
      );
    }),
  );
}
